import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import _ from 'lodash';
import { getBricks } from '../../utils/api';
import { loadImage } from '../../utils/imageCache';

export interface Brick {
  type: string;
  image: string;
}

export interface BrickSeries {
  _id: string;
  name: string;
  thumbnail: string;
  bricks: Brick[];
}

interface BricksState {
  loading: boolean;
  brickSeries: Record<string, BrickSeries>;
  error: string | null;
}

const initialState: BricksState = {
  loading: false,
  brickSeries: {},
  error: null,
};

// Async thunk for fetching bricks
export const fetchBricks = createAsyncThunk(
  'bricks/fetchBricks',
  async () => {
    const { brickCollections } = await getBricks("5f95c5aa3755f3ed1d71b3d3");
    return _.keyBy(brickCollections, (b: BrickSeries) => b._id);
  }
);

// Async thunk for preparing series (loading images)
export const prepareSeries = createAsyncThunk(
  'bricks/prepareSeries',
  async (seriesId: string, { getState }) => {
    const state = getState() as { bricks: BricksState };
    const series = state.bricks.brickSeries[seriesId];
    
    if (series) {
      await Promise.all(series.bricks.map((b: Brick) => loadImage(b.image)));
    }

    return seriesId;
  }
);

const bricksSlice = createSlice({
  name: 'bricks',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchBricks.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBricks.fulfilled, (state, action) => {
        state.loading = false;
        state.brickSeries = action.payload;
      })
      .addCase(fetchBricks.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch bricks';
      })
      .addCase(prepareSeries.pending, (state) => {
        state.loading = true;
      })
      .addCase(prepareSeries.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(prepareSeries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load brick images';
      });
  },
});

// Selectors
export const selectBrickSeries = (state: { bricks: BricksState }) => state.bricks.brickSeries;
export const selectSeriesList = createSelector(
  [selectBrickSeries],
  (brickSeries) => Object.values(brickSeries)
);
export const selectBricksLoading = (state: { bricks: BricksState }) => state.bricks.loading;

export default bricksSlice.reducer;